import React, { useState, useRef, forwardRef, useImperativeHandle, useMemo } from "react"
import { createPortal } from "react-dom"
import { useTranslation } from "react-i18next"
import { colors as designColors } from "@/components/colors"
import "./performanceGraph.scss"
import { ExportFileFunctionType } from "@/app/report/common/type"
import { PDF } from "@/app/report/common/export/pdf"
import { exportSVG } from "@/app/report/common/export/export"
import { getDateTimeString } from "@/app/report/common/tools"
import { EXPORT_TYPE } from "@/app/report/common/type"

interface PerformanceGraphProps {
  performanceData: {
    speed?: number[]
    accel?: number[]
    hr?: number[]
  }
  actions: Array<{
    actionType: string
    startOccurrence: number
    endOccurrence: number
    duration: number
    label?: string
  }>
  sessions?: Array<{
    start: number
    end: number
    name: string
  }>
  width?: number
  height?: number
  colors?: {
    speed?: string
    accel?: string
    hr?: string
  }
  actionColors?: Record<string, string>
  manualScale?: {
    speed?: {
      active: boolean
      value: number
    }
    accel?: {
      active: boolean
      value: number
    }
    hr?: {
      active: boolean
      value: number
    }
  }
  totalOccurrence: number
  timeRange?: {
    start: number
    end: number
  }
  maxTime?: number
  boundaryMin?: number
  boundaryMax?: number
  onTimeRangeChange?: (timeRange: { start: number; end: number }) => void
  downsampled1HzData?: {
    speed: { values: number[]; points: { value: number; timeIndex: number }[] }
    accel: { values: number[]; points: { value: number; timeIndex: number }[] }
    hr: { values: number[]; points: { value: number; timeIndex: number }[] }
  }
}

function PerformanceGraph(
  {
    performanceData,
    actions = [],
    sessions = [],
    width = 1000,
    height = 200,
    colors = {
      speed: designColors.blue["800"],
      accel: designColors.orange["700"],
      hr: designColors.red["700"],
    },
    actionColors = {},
    manualScale,
    totalOccurrence,
    timeRange,
    maxTime,
    boundaryMin,
    boundaryMax,
    onTimeRangeChange,
    downsampled1HzData,
  }: PerformanceGraphProps,
  ref: React.ForwardedRef<{
    exportFile: (args: ExportFileFunctionType) => Promise<void>
  }>,
) {
  const { t } = useTranslation()
  const [hoveredData, setHoveredData] = useState<{ x: number; y: number; data: any } | null>(null)
  const [isDragging, setIsDragging] = useState(false)
  const [dragStartX, setDragStartX] = useState(0)
  const [dragStartRange, setDragStartRange] = useState({ start: 0, end: 0 })
  const svgRef = useRef<SVGSVGElement>(null)
  const startRange = timeRange ? timeRange.start : 0
  const endRange = timeRange ? timeRange.end : totalOccurrence
  const rangeWidth = endRange - startRange
  const maxTimeValue = maxTime || totalOccurrence

  const TEN_MINUTES_IN_100MS = 6000
  const is1HzMode = rangeWidth >= TEN_MINUTES_IN_100MS

  type DownsampledPoint = { value: number; timeIndex: number }

  const normalizeData10Hz = (
    data: number[],
    maxValue: number,
    minValue: number,
    isCentered: boolean = false,
  ) => {
    const range = maxValue - minValue || 1
    const result: Array<{ x: number; y: number; value: number }> = []

    for (let i = 0; i < data.length; i++) {
      const value = data[i]

      if (value === null || value === undefined) {
        continue
      }

      const dataTime = i

      if (dataTime < startRange || dataTime > endRange) {
        continue
      }

      const x = ((dataTime - startRange) / rangeWidth) * width
      let y: number

      if (isCentered) {
        const centerY = height / 2
        const normalizedValue = value / Math.max(Math.abs(maxValue), Math.abs(minValue))
        y = centerY - normalizedValue * (height / 2)
      } else {
        y = height - ((value - minValue) / range) * height
      }

      result.push({ x, y, value })
    }

    return result
  }

  const normalizeData1Hz = (
    downsampledData: DownsampledPoint[],
    _originalDataLength: number,
    maxValue: number,
    minValue: number,
    isCentered: boolean = false,
  ) => {
    const range = maxValue - minValue || 1
    const result: Array<{ x: number; y: number; value: number }> = []

    for (const point of downsampledData) {
      const dataTime = point.timeIndex

      if (dataTime < startRange || dataTime > endRange) {
        continue
      }

      const x = ((dataTime - startRange) / rangeWidth) * width
      let y: number

      if (isCentered) {
        const centerY = height / 2
        const normalizedValue = point.value / Math.max(Math.abs(maxValue), Math.abs(minValue))
        y = centerY - normalizedValue * (height / 2)
      } else {
        y = height - ((point.value - minValue) / range) * height
      }

      result.push({ x, y, value: point.value })
    }

    return result
  }

  const getMinMax = (data: number[]) => {
    if (!data || data.length === 0) {
      return { min: 0, max: 100 }
    }
    const validData = data.filter((v) => v !== null && v !== undefined)
    if (validData.length === 0) {
      return { min: 0, max: 100 }
    }

    // spread operator 대신 반복문 사용 (대용량 배열에서 스택 오버플로우 방지)
    let min = validData[0]
    let max = validData[0]
    for (let i = 1; i < validData.length; i++) {
      if (validData[i] < min) min = validData[i]
      if (validData[i] > max) max = validData[i]
    }

    return { min, max }
  }

  useImperativeHandle(ref, () => ({
    exportFile: async (args: ExportFileFunctionType) => {
      const { exportType, reportType } = args
      const pdf = await PDF.create()
      const FILE_NAME_HEADER = `${reportType}_report_performance_profile`

      if (!svgRef.current) {
        return
      }

      const fileName = `${getDateTimeString()}_${FILE_NAME_HEADER}`

      await exportSVG(
        svgRef.current,
        fileName,
        "white",
        exportType === "png" ? EXPORT_TYPE.PNG : EXPORT_TYPE.PDF,
        pdf,
      )

      if (exportType === "pdf") {
        pdf.save(`${fileName}.pdf`)
      }
    },
  }))

  const metrics = useMemo(() => {
    const result: Array<{
      name: string
      data: Array<{ x: number; y: number; value: number }>
      color: string
      min: number
      max: number
      unit: string
    }> = []

    if (
      performanceData &&
      performanceData.speed &&
      performanceData.speed.length > 0 &&
      performanceData.speed.some((v) => v !== null && v !== undefined)
    ) {
      const visibleData = performanceData.speed.filter((_, index) => {
        const dataTime = index

        return dataTime >= startRange && dataTime <= endRange
      })
      const { max } = getMinMax(visibleData.length > 0 ? visibleData : performanceData.speed)
      const finalMax = manualScale?.speed?.active ? manualScale.speed.value : max
      const finalMin = 0

      const normalizedData =
        is1HzMode && downsampled1HzData?.speed?.points
          ? normalizeData1Hz(
              downsampled1HzData.speed.points,
              performanceData.speed.length,
              finalMax,
              finalMin,
            )
          : normalizeData10Hz(performanceData.speed, finalMax, finalMin)

      result.push({
        name: "speed",
        data: normalizedData,
        color: colors.speed || "",
        min: finalMin,
        max: finalMax,
        unit: "km/h",
      })
    }

    if (
      performanceData &&
      performanceData.accel &&
      performanceData.accel.length > 0 &&
      performanceData.accel.some((v) => v !== null && v !== undefined)
    ) {
      const visibleData = performanceData.accel.filter((_, index) => {
        const dataTime = index

        return dataTime >= startRange && dataTime <= endRange
      })

      const { min, max } = getMinMax(visibleData.length > 0 ? visibleData : performanceData.accel)

      const dataAbsMax = Math.max(Math.abs(min), Math.abs(max))
      const absMax = manualScale?.accel?.active ? manualScale.accel.value : dataAbsMax

      const normalizedData =
        is1HzMode && downsampled1HzData?.accel?.points
          ? normalizeData1Hz(
              downsampled1HzData.accel.points,
              performanceData.accel.length,
              absMax,
              -absMax,
              true,
            )
          : normalizeData10Hz(performanceData.accel, absMax, -absMax, true)

      result.push({
        name: "accel",
        data: normalizedData,
        color: colors.accel || "",
        min: -absMax,
        max: absMax,
        unit: "m/s²",
      })
    }

    if (
      performanceData &&
      performanceData.hr &&
      performanceData.hr.length > 0 &&
      performanceData.hr.some((v) => v !== null && v !== undefined)
    ) {
      const visibleData = performanceData.hr.filter((_, index) => {
        const dataTime = index

        return dataTime >= startRange && dataTime <= endRange
      })
      const { max } = getMinMax(visibleData.length > 0 ? visibleData : performanceData.hr)
      const finalMax = manualScale?.hr?.active ? manualScale.hr.value : max
      const finalMin = 0

      const normalizedData =
        is1HzMode && downsampled1HzData?.hr?.points
          ? normalizeData1Hz(
              downsampled1HzData.hr.points,
              performanceData.hr.length,
              finalMax,
              finalMin,
            )
          : normalizeData10Hz(performanceData.hr, finalMax, finalMin)

      result.push({
        name: "hr",
        data: normalizedData,
        color: colors.hr || "",
        min: finalMin,
        max: finalMax,
        unit: "bpm",
      })
    }

    return result
  }, [
    performanceData,
    is1HzMode,
    downsampled1HzData,
    startRange,
    endRange,
    totalOccurrence,
    width,
    height,
    manualScale,
    colors,
    rangeWidth,
  ])

  const actionRects = useMemo(
    () =>
      actions
        .map((action) => {
          const startIn100ms = action.startOccurrence * 10
          const endIn100ms = action.endOccurrence * 10

          if (endIn100ms < startRange || startIn100ms > endRange) {
            return null
          }

          const clippedStart = Math.max(startIn100ms, startRange)
          const clippedEnd = Math.min(endIn100ms, endRange)

          const startX = ((clippedStart - startRange) / rangeWidth) * width
          const endX = ((clippedEnd - startRange) / rangeWidth) * width
          const calculatedWidth = endX - startX

          const actionColor = actionColors[action.actionType] || "rgba(128, 128, 128, 0.15)"

          return {
            ...action,
            x: startX,
            width: calculatedWidth,
            color: actionColor,
          }
        })
        .filter((action) => action !== null),
    [actions, startRange, endRange, rangeWidth, width, actionColors],
  )

  if (metrics.length === 0) {
    return (
      <div className="performance-graph-container no-data">
        <div className="no-data-message">{t("messages.noDataAvailable")}</div>
      </div>
    )
  }

  const hasSpeed = metrics.some((m) => m.name === "speed")
  const hasAccel = metrics.some((m) => m.name === "accel")
  const hasHr = metrics.some((m) => m.name === "hr")

  const leftPadding = hasSpeed ? 45 : 20
  const rightPadding = (hasAccel ? 50 : 0) + (hasHr ? 40 : 0) + 10

  const handleMouseDown = (e: React.MouseEvent<SVGSVGElement>) => {
    if (!onTimeRangeChange) {
      return
    }

    setIsDragging(true)
    setDragStartX(e.clientX)
    setDragStartRange({ start: startRange, end: endRange })
    e.preventDefault()
  }

  const handleMouseMoveOnDrag = (e: React.MouseEvent<SVGSVGElement>) => {
    if (isDragging && onTimeRangeChange && svgRef.current) {
      const rect = svgRef.current.getBoundingClientRect()
      const deltaX = e.clientX - dragStartX
      const deltaTime = -(deltaX / rect.width) * rangeWidth

      let newStart = dragStartRange.start + deltaTime
      let newEnd = dragStartRange.end + deltaTime

      newStart = Math.round(newStart)
      newEnd = Math.round(newEnd)

      const minBound = boundaryMin ?? 0
      const maxBound = boundaryMax ?? maxTimeValue

      if (newStart < minBound) {
        newStart = minBound
        newEnd = minBound + rangeWidth
      } else if (newEnd > maxBound) {
        newEnd = maxBound
        newStart = maxBound - rangeWidth
      }

      onTimeRangeChange({ start: newStart, end: newEnd })
    }
  }

  const handleMouseUp = () => {
    setIsDragging(false)
  }

  const handleMouseMove = (e: React.MouseEvent<SVGSVGElement>) => {
    if (isDragging) {
      handleMouseMoveOnDrag(e)

      return
    }

    if (!svgRef.current) {
      return
    }

    const rect = svgRef.current.getBoundingClientRect()
    const x = ((e.clientX - rect.left) / rect.width) * width
    const mouseY = ((e.clientY - rect.top) / rect.height) * height
    const timeAtMouse = startRange + (x / width) * rangeWidth

    const HOVER_THRESHOLD = 15
    let isNearLine = false

    const dataAtTime: any = {}
    metrics.forEach((metric) => {
      if (metric.data.length > 0) {
        const closest = metric.data.reduce((prev, curr) => {
          return Math.abs(curr.x - x) < Math.abs(prev.x - x) ? curr : prev
        })

        const distance = Math.abs(closest.y - mouseY)
        if (distance <= HOVER_THRESHOLD) {
          isNearLine = true
        }

        dataAtTime[metric.name] = {
          value: closest.value,
          unit: metric.unit,
          color: metric.color,
        }
      }
    })

    const isOverAction = actionRects.some((action) => {
      if (!action) {
        return false
      }

      return x >= action.x && x <= action.x + action.width
    })

    if (!isNearLine && !isOverAction) {
      setHoveredData(null)

      return
    }

    const activeActions = actionRects
      .filter((action) => {
        if (!action) {
          return false
        }

        return x >= action.x && x <= action.x + action.width
      })
      .map((action) => ({
        type: action.label || action.actionType,
        duration: action.duration,
        color: action.color,
      }))

    const activeSessions = sessions.filter((session) => {
      return timeAtMouse >= session.start && timeAtMouse <= session.end
    })

    const totalSeconds = timeAtMouse / 10
    const minutes = Math.floor(totalSeconds / 60)
    const seconds = Math.floor(totalSeconds % 60)
    const deciseconds = Math.floor((totalSeconds % 1) * 10)
    const timeLabel = `${minutes}'${seconds.toString().padStart(2, "0")}''${deciseconds}`

    setHoveredData({
      x: e.clientX,
      y: e.clientY,
      data: { ...dataAtTime, time: timeLabel, actions: activeActions, sessions: activeSessions },
    })
  }

  const handleMouseLeave = () => {
    setHoveredData(null)
  }

  return (
    <div
      className={`performance-graph-container ${hasSpeed ? "has-speed" : "has-no-speed"}`}
      style={{
        height: `${height + 80}px`,
        paddingRight: `${rightPadding}px`,
      }}>
      <svg
        ref={svgRef}
        className={`performance-graph-svg ${isDragging ? "dragging" : ""}`}
        width="100%"
        height={height}
        preserveAspectRatio="none"
        viewBox={`0 0 ${width} ${height}`}
        onMouseDown={handleMouseDown}
        onMouseMove={handleMouseMove}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseLeave}
        style={{ cursor: isDragging ? "grabbing" : onTimeRangeChange ? "grab" : "crosshair" }}>
        <defs>
          <clipPath id="graph-clip">
            <rect x="0" y="0" width={width} height={height} />
          </clipPath>
        </defs>

        {[0, 16.67, 33.33, 50, 66.67, 83.33, 100].map((percent) => (
          <line
            key={`h-${percent}`}
            className={`grid-line-horizontal ${percent === 100 ? "bottom-line" : ""}`}
            x1="0"
            y1={height * (percent / 100)}
            x2={width}
            y2={height * (percent / 100)}
          />
        ))}

        {metrics.find((m) => m.name === "accel") && (
          <line
            className="grid-line-horizontal accel-center"
            x1="0"
            y1={height / 2}
            x2={width}
            y2={height / 2}
          />
        )}

        {actionRects.map((action, index) => (
          <g key={`action-${index}`}>
            <rect
              x={action.x}
              y={0}
              width={action.width}
              height={height}
              fill={action.color}
              className="action-rect"
              opacity={0.2}
            />
            <line
              x1={action.x}
              y1={0}
              x2={action.x}
              y2={height}
              stroke={action.color}
              strokeWidth="1"
              opacity={0.15}
            />
            <line
              x1={action.x + action.width}
              y1={0}
              x2={action.x + action.width}
              y2={height}
              stroke={action.color}
              strokeWidth="1"
              opacity={0.1}
            />
          </g>
        ))}

        <g clipPath="url(#graph-clip)">
          {metrics.map((metric) => {
            const points = metric.data.map((p) => `${p.x},${p.y}`).join(" ")

            return (
              <g key={metric.name}>
                <polyline
                  className={`graph-line graph-line-${metric.name}`}
                  points={points}
                  stroke={metric.color}
                  strokeWidth="2"
                  fill="none"
                />
              </g>
            )
          })}
        </g>
      </svg>

      <div
        className="x-axis-container"
        style={{
          left: `${leftPadding}px`,
          right: `${rightPadding}px`,
        }}>
        {[0, 12.5, 25, 37.5, 50, 62.5, 75, 87.5, 100].map((percent) => {
          const timeValue = startRange + (rangeWidth * percent) / 100
          const totalSeconds = timeValue / 10
          const totalMinutes = Math.floor(totalSeconds / 60)
          const timeLabel = `${totalMinutes}'`

          return (
            <span key={percent} className="x-axis-label" style={{ left: `${percent}%` }}>
              {timeLabel}
            </span>
          )
        })}
      </div>

      {metrics.find((m) => m.name === "speed") && (
        <div className="y-axis-left">
          <div className="y-axis-title">Speed (km/h)</div>
          <div className="y-axis-labels">
            {[
              { value: 100, position: 0 },
              { value: 83.33, position: 16.67 },
              { value: 66.67, position: 33.33 },
              { value: 50, position: 50 },
              { value: 33.33, position: 66.67 },
              { value: 16.67, position: 83.33 },
              { value: 0, position: 100 },
            ].map(({ value, position }) => {
              const speedMetric = metrics.find((m) => m.name === "speed")
              if (!speedMetric) {
                return null
              }

              const actualValue =
                speedMetric.max - ((speedMetric.max - speedMetric.min) * position) / 100

              return (
                <span key={value} className="y-axis-label" style={{ top: `${position}%` }}>
                  {actualValue?.toFixed(0)}
                </span>
              )
            })}
          </div>
        </div>
      )}

      {metrics.find((m) => m.name === "accel") && (
        <div className={`y-axis-accel ${hasHr ? "with-hr" : "without-hr"}`}>
          <div className="y-axis-title">Accel (m/s²)</div>
          <div className="y-axis-labels">
            {(() => {
              const accelMetric = metrics.find((m) => m.name === "accel")
              if (!accelMetric) {
                return null
              }

              return [
                { value: accelMetric.max, position: 0 },
                { value: (accelMetric.max * 2) / 3, position: 16.67 },
                { value: accelMetric.max / 3, position: 33.33 },
                { value: 0, position: 50 },
                { value: -accelMetric.max / 3, position: 66.67 },
                { value: (-accelMetric.max * 2) / 3, position: 83.33 },
                { value: -accelMetric.max, position: 100 },
              ].map(({ value, position }) => {
                return (
                  <span
                    key={position}
                    className="y-axis-label"
                    style={{
                      top: `${position}%`,
                    }}>
                    {value?.toFixed(1)}
                  </span>
                )
              })
            })()}
          </div>
        </div>
      )}

      {metrics.find((m) => m.name === "hr") && (
        <div className="y-axis-hr">
          <div className="y-axis-title">HR (bpm)</div>
          <div className="y-axis-labels">
            {[
              { value: 100, position: 0 },
              { value: 83.33, position: 16.67 },
              { value: 66.67, position: 33.33 },
              { value: 50, position: 50 },
              { value: 33.33, position: 66.67 },
              { value: 16.67, position: 83.33 },
              { value: 0, position: 100 },
            ].map(({ value, position }) => {
              const hrMetric = metrics.find((m) => m.name === "hr")
              if (!hrMetric) {
                return null
              }
              const actualValue = hrMetric.max - ((hrMetric.max - hrMetric.min) * position) / 100

              return (
                <span
                  key={value}
                  className="y-axis-label"
                  style={{
                    top: `${position}%`,
                  }}>
                  {actualValue?.toFixed(0)}
                </span>
              )
            })}
          </div>
        </div>
      )}

      {hoveredData &&
        createPortal(
          <div
            style={{
              position: "fixed",
              left: hoveredData.x + 10,
              top: hoveredData.y + 20,
              zIndex: 9999,
              minWidth: 180,
              padding: 12,
              border: `1px solid ${designColors.neutral.grey["300"]}`,
              borderRadius: 8,
              background: designColors.neutral.white,
              boxShadow: "0 4px 12px rgb(0 0 0 / 15%)",
              fontSize: 12,
              pointerEvents: "none",
            }}>
            {/* Session Names */}
            {hoveredData.data.sessions && hoveredData.data.sessions.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginBottom: 8,
                  paddingBottom: 8,
                  gap: 4,
                  borderBottom: `1px solid ${designColors.neutral.grey["300"]}`,
                }}>
                {hoveredData.data.sessions.map((session: any, idx: number) => (
                  <div
                    key={idx}
                    style={{
                      color: designColors.neutral.grey["1000"],
                      fontWeight: 600,
                      fontSize: 14,
                    }}>
                    {session.name}
                  </div>
                ))}
              </div>
            )}

            {/* Time */}
            <div
              style={{
                marginBottom: 12,
                color: designColors.neutral.grey["800"],
                fontWeight: 500,
                fontSize: 11,
              }}>
              {hoveredData.data.time}
            </div>

            {/* Metrics */}
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {hoveredData.data.speed && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: designColors.neutral.grey["1000"],
                      fontSize: 12,
                    }}>
                    <span
                      style={{
                        flexShrink: 0,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: hoveredData.data.speed.color,
                      }}></span>
                    Speed
                  </span>
                  <span
                    style={{
                      color: designColors.neutral.grey["1000"],
                      fontWeight: 600,
                      fontSize: 13,
                      whiteSpace: "nowrap",
                    }}>
                    {hoveredData.data.speed.value?.toFixed(1)} {hoveredData.data.speed.unit}
                  </span>
                </div>
              )}
              {hoveredData.data.accel && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: designColors.neutral.grey["1000"],
                      fontSize: 12,
                    }}>
                    <span
                      style={{
                        flexShrink: 0,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: hoveredData.data.accel.color,
                      }}></span>
                    Acceleration
                  </span>
                  <span
                    style={{
                      color: designColors.neutral.grey["1000"],
                      fontWeight: 600,
                      fontSize: 13,
                      whiteSpace: "nowrap",
                    }}>
                    {hoveredData.data.accel.value?.toFixed(2)} {hoveredData.data.accel.unit}
                  </span>
                </div>
              )}
              {hoveredData.data.hr && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    gap: 12,
                  }}>
                  <span
                    style={{
                      display: "flex",
                      alignItems: "center",
                      gap: 6,
                      color: designColors.neutral.grey["1000"],
                      fontSize: 12,
                    }}>
                    <span
                      style={{
                        flexShrink: 0,
                        width: 8,
                        height: 8,
                        borderRadius: "50%",
                        backgroundColor: hoveredData.data.hr.color,
                      }}></span>
                    HR
                  </span>
                  <span
                    style={{
                      color: designColors.neutral.grey["1000"],
                      fontWeight: 600,
                      fontSize: 13,
                      whiteSpace: "nowrap",
                    }}>
                    {hoveredData.data.hr.value?.toFixed(0)} {hoveredData.data.hr.unit}
                  </span>
                </div>
              )}
            </div>

            {hoveredData.data.actions && hoveredData.data.actions.length > 0 && (
              <div
                style={{
                  display: "flex",
                  flexDirection: "column",
                  marginTop: 12,
                  paddingTop: 12,
                  gap: 8,
                  borderTop: `1px solid ${designColors.neutral.grey["300"]}`,
                }}>
                {hoveredData.data.actions.map((action: any, idx: number) => (
                  <div
                    key={idx}
                    style={{
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "space-between",
                      gap: 12,
                    }}>
                    <span
                      style={{
                        display: "flex",
                        alignItems: "center",
                        gap: 6,
                        color: designColors.neutral.grey["1000"],
                        fontSize: 12,
                      }}>
                      <span
                        style={{
                          flexShrink: 0,
                          width: 8,
                          height: 8,
                          borderRadius: "50%",
                          backgroundColor: action.color,
                        }}></span>
                      {action.type}
                    </span>
                    <span
                      style={{
                        color: designColors.neutral.grey["1000"],
                        fontWeight: 600,
                        fontSize: 13,
                        whiteSpace: "nowrap",
                      }}>
                      {action.duration}
                      {t("sec")}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>,
          document.body,
        )}
    </div>
  )
}

export default forwardRef(PerformanceGraph)
